<template>
  <b-container
    class="mb-5"
    fluid
  >
    <div class="d-flex justify-content-end mb-0 pb-0">
      <b-breadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>
    <b-container fluid>
      <b-row>
        <b-col
          cols="12"
          md="10"
          class="px-1"
          order="2"
          order-md="1"
        >
          <b-form-group
            label="Search"
            label-for="filter_search"
            label-class="font-weight-bolder"
          >
            <b-input
              id="filter_search"
              v-model="tableDisbursement.filter.search"
              type="text"
              size="sm"
              placeholder="search here"
              debounce="1000"
              autocomplete="off"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
          class="px-1"
          order="1"
          order-md="2"
        >
          <b-form-group
            label="Channel"
            label-for="filter_channel"
            label-class="font-weight-bolder"
          >
            <b-form-select
              id="filter_channel"
              v-model="tableDisbursement.filter.channel"
              size="sm"
              :options="tableDisbursement.filterOptions.channels"
            >
              <template #first>
                <b-form-select-option
                  :value="null"
                  disabled
                >
                  -- select channel here --
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          class="px-1 mb-2"
          order="3"
        >
          <b-button
            size="sm"
            type="button"
            variant="primary"
            class="mr-1"
            :disabled="tableDisbursement.busy"
            @click="tableDisbursementRefresh"
          >
            <b-icon
              icon="arrow-clockwise"
              :animation="(tableDisbursement.busy ? 'spin' : null)"
            /> {{ tableDisbursement.busy ? 'Loading' : 'Reload' }}
          </b-button>
        </b-col>

      </b-row>

      <b-row class="mt-1">
        <b-col
          cols="12"
          class="px-1"
        >
          <b-table
            ref="tableDisbursement"
            hover
            small
            striped
            bordered
            responsive
            show-empty
            :stacked="isMobile()"
            :per-page="tableDisbursement.perPage"
            :current-page="tableDisbursement.currentPage"
            :items="tableDisbursementProvider"
            :fields="tableDisbursement.fields"
            :sort-by.sync="tableDisbursement.sortBy"
            :sort-desc.sync="tableDisbursement.sortDesc"
            :sort-direction="tableDisbursement.sortDirection"
            :filter="tableDisbursement.filter"
            :filter-included-fields="tableDisbursement.filterOn"
            :busy="tableDisbursement.busy"
          >
            <template #cell(index)="{ index }">
              {{ tableNumber(tableDisbursement, index) }}
            </template>

            <template #cell()="{ value }">
              <div class="text-wrap text-md-nowrap">
                {{ value }}
              </div>
            </template>

            <template #cell(active)="{ value }">
              {{ Number(value) === 1 ? 'Yes' : 'No' }}
            </template>

            <template #cell(amount)="{ value }">
              <div class="text-right">
                {{ numberFormatter(value) }}
              </div>
            </template>

            <template #cell(fee)="{ item }">
              <div class="text-right">
                {{ numberFormatter(parseFloat(item.fee) + parseFloat(item.percentage_fee)) }}
              </div>
            </template>

            <template #cell(total)="{ item }">
              <div class="text-right">
                {{ numberFormatter(parseFloat(item.amount) + (parseFloat(item.fee) + parseFloat(item.percentage_fee))) }}
              </div>
            </template>

            <template #cell(action)="row">
              <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
                <b-button
                  v-if="row.item.webhook"
                  size="sm"
                  :variant="row.detailsShowing ? 'danger' : 'primary' "
                  @click="row.toggleDetails"
                >
                  {{ row.detailsShowing ? 'Hide' : 'Show' }} Webhook
                </b-button>
              </div>
            </template>

            <template #row-details="{ item }">
              <b-form-group
                label="Webhook"
                label-class="font-weight-bolder"
                class="mb-2 px-5"
              >
                <vue-json-pretty
                  :data="item.webhook"
                />
              </b-form-group>
            </template>

            <template #table-busy>
              <div class="text-center py-5">
                <b-icon
                  icon="stopwatch"
                  font-scale="5"
                  animation="cylon"
                />
                <p class="h3 py-2">
                  <strong>Loading . . .</strong>
                </p>
              </div>
            </template>

          </b-table>
        </b-col>

        <b-col
          cols="12"
          md="6"
          class="mt-1 mb-2 px-1"
        >
          <b-select
            v-model="tableDisbursement.perPage"
            :options="tableDisbursement.pageOptions"
            size="sm"
            class="w-100 w-md-25"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
        >
          <b-pagination
            v-model="tableDisbursement.currentPage"
            pills
            last-number
            first-number
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
            :total-rows="tableDisbursement.totalRows"
            :per-page="tableDisbursement.perPage"
          />
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>

<script>
// import QUERY from 'lodash'
import { core } from '@/config/pluginInit'
import { SAdminDisbursement } from '@/services'
import formatter from '@/mixins/formatter'
import Ripple from 'vue-ripple-directive'
import misc from '@/mixins/misc'
import createDate from 'moment'

export default {
  name: 'AdminDisbursements',

  middleware: ['auth', 'admin'],

  directives: {
    Ripple
  },

  mixins: [misc, formatter],

  metaInfo: () => ({
    title: 'Disbursements'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      tableDisbursement: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: null,
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: null,
          channel: 'All',
          date: createDate().format('YYYY-MM-DD')
        },
        filterOptions: {
          channels: ['All', 'Instapay', 'Pesonet'],
          statuses: ['All', 'Pending', 'Completed']
        },
        filterOn: [],
        busy: false,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 14, key: 'action', class: 'text-center min-w-150px' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeFormatter, sortable: true },
          { mobile: 2, key: 'partner_reference', class: 'min-w-350px' },
          { mobile: 3, key: 'channel', class: 'min-w-125px' },
          { mobile: 5, key: 'bank_name', class: 'min-w-250px' },
          { mobile: 6, key: 'account_number', class: 'min-w-250px' },
          { mobile: 7, key: 'account_name', class: 'min-w-250px' },
          { mobile: 8, key: 'amount', class: 'min-w-150px', formatter: this.numberFormatter },
          { mobile: 9, key: 'fee', class: 'min-w-150px', formatter: this.numberFormatter },
          { mobile: 10, key: 'total', class: 'min-w-150px' },
          { mobile: 11, key: 'status', class: 'text-center min-w-100px' },
          { mobile: 12, key: 'reference_number', class: 'min-w-350px' },
          { mobile: 13, key: 'completed_at', class: 'min-w-150px', formatter: this.dateTimeFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  mounted () {
    core.index()
  },

  methods: {

    async tableDisbursementProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableDisbursement.busy = true
      return await SAdminDisbursement.getReports(
        this.objectToUrl({
          page: currentPage,
          per_page: perPage,
          sort: sortBy,
          sort_desc: sortDesc,
          filter_text: filter.search,
          filter_channel: filter.channel
        })
      ).then(({ data }) => {
        this.tableDisbursement.totalRows = data.total_rows
        return data.items
      }).catch(() => []).finally(() => {
        this.tableDisbursement.busy = false
      })
    },

    tableDisbursementRefresh () {
      this.$refs.tableDisbursement.refresh()
    }
  }
}
</script>
